/**
 * The purchased blocks (mission 9 WP13 T080; sheet 50 callout 6; B.4, NF.9; contracts/ui-api.md
 * "Sheet 50", "Accessibility").
 *
 * A `<table>` with `<th scope="col">` (Credits, Remaining, Purchased, Expires), the figures and dates in
 * mono. A block the read flags `expiring` (inside the expiring notice) carries `blockExpiring`, the days
 * counted from now to its own expiry. With no granted block the table is replaced by `blocksEmpty`, never
 * an empty grid (§7.7).
 */
import type { CreditBlock } from '@/lib/api/billing'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Mono, count, date } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'

const DAY_MS = 24 * 60 * 60 * 1000

/** Whole days from now to `iso`, rounded up, never below 1. */
function daysUntil(iso: string): number {
  return Math.max(1, Math.ceil((Date.parse(iso) - Date.now()) / DAY_MS))
}

export function BlocksTable({ blocks }: { blocks: CreditBlock[] }) {
  return (
    <section className="flex flex-col gap-3">
      <h4 className="text-body font-medium text-navy-950">{copyText('blocksTitle')}</h4>
      {blocks.length === 0 ? (
        <p className="text-body-sm text-navy-600">{copyText('blocksEmpty')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow dense>
              <TableHead className="text-right">{copyText('blocksColCredits')}</TableHead>
              <TableHead className="text-right">{copyText('blocksColRemaining')}</TableHead>
              <TableHead>{copyText('blocksColPurchased')}</TableHead>
              <TableHead>{copyText('blocksColExpires')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {blocks.map((block) => (
              <TableRow key={block.id} dense>
                <TableCell numeric>{count(block.credits)}</TableCell>
                <TableCell numeric>{count(block.remaining)}</TableCell>
                <TableCell>
                  <Mono className="text-body-sm">{date(block.purchased_at)}</Mono>
                </TableCell>
                <TableCell>
                  <Mono className="text-body-sm">{date(block.expires_at)}</Mono>
                  {block.expiring ? (
                    <span className="ml-2 inline-flex h-[22px] items-center rounded-sm border border-[var(--color-warning-200)] bg-[var(--color-warning-50)] px-2 text-caption font-medium text-[var(--color-warning-800)]">
                      {PROVISIONAL_COPY.blockExpiring.render({ days: daysUntil(block.expires_at) })}
                    </span>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  )
}
